import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFeature } from '../../contexts/FeatureContext';
import { 
  Menu, 
  X, 
  LogOut, 
  User, 
  Home, 
  Star,
  PenTool, 
  Moon, 
  Sun, 
  ChevronDown,
  MessageSquare,
  Video,
  Grid,
  BarChart,
  Globe
} from 'lucide-react';

const Header = () => {
  const { currentUser, logout } = useAuth();
  const { isFeatureEnabled } = useFeature();
  const navigate = useNavigate();
  
  // State management as requested
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check if dark mode preference is stored
    const savedMode = localStorage.getItem('darkMode');
    // Default to false (light mode) unless explicitly set to true
    return savedMode === 'true';
  });

  // Toggle the menu
  const toggleMenu = () => setIsOpen(!isOpen);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('darkMode', String(newMode));
    document.documentElement.classList.toggle('dark', newMode);
  };

  // Initialize dark mode on component mount
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const tools = [
    { id: 'youtube-reel', name: 'YouTube Reel Creator', icon: Video, path: '/youtube-reel' },
    { id: 'form-connect', name: 'WhatsApp FormConnect', icon: MessageSquare, path: '/form-connect' },
    { id: 'social-media-content', name: 'Social Media Content', icon: Star, path: '/social-media-content' },
    { id: 'digital-identity', name: 'Digital Identity Hub', icon: User, path: '/digital-identity' },
  ];

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-md' 
        : 'bg-white dark:bg-gray-900'
    }`}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className={`p-2 rounded-lg mr-2 transition-all duration-300 ${
              isScrolled ? 'bg-indigo-100 dark:bg-indigo-900/40' : 'bg-indigo-50 dark:bg-indigo-900/20'
            } group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800/40`}>
              <PenTool className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <span className="text-lg font-bold text-gray-900 dark:text-white">KangleiCode Tools</span>
              <span className="hidden sm:block text-xs text-gray-500 dark:text-gray-400">A KangleiCode Product</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            <Link 
              to="/" 
              className="px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
            >
              <Home className="h-4 w-4 mr-1.5" />
              Home
            </Link>
            <a 
              href="https://kangleicode.in" 
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
            >
              <Globe className="h-4 w-4 mr-1.5" />
              KangleiCode Home
            </a>
            
            {/* Tools Dropdown */}
            <div className="relative group">
              <button className="px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors">
                <Grid className="h-4 w-4 mr-1.5" />
                <span>Tools</span>
                <ChevronDown className="h-4 w-4 ml-1 opacity-70" />
              </button>
              <div className="absolute left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-20 border border-gray-200 dark:border-gray-700">
                {tools.filter(tool => isFeatureEnabled(tool.id)).map((tool) => (
                  <Link 
                    key={tool.path}
                    to={tool.path} 
                    className="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                  >
                    <tool.icon className="h-4 w-4 mr-2 text-indigo-500 dark:text-indigo-400" />
                    {tool.name}
                  </Link>
                ))}
                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                {!currentUser && (
                  <div className="px-4 py-2">
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      <Link to="/signin" className="text-indigo-600 dark:text-indigo-400 font-medium">Sign in</Link> to access all tools
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {currentUser && (
              <Link 
                to="/dashboard" 
                className="px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
              >
                <BarChart className="h-4 w-4 mr-1.5" />
                Dashboard
              </Link>
            )}
            
            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="ml-1 p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5 text-amber-500" />
              ) : (
                <Moon className="h-5 w-5 text-indigo-600" />
              )}
            </button>
            
            {currentUser ? (
              <div className="relative group ml-1">
                <button className="flex items-center px-3 py-2 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800/40 transition-colors focus:outline-none">
                  <span className="mr-1 flex items-center">
                    <User className="h-4 w-4 mr-1.5" />
                    <span className="max-w-[100px] truncate">{currentUser.displayName || 'Account'}</span>
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-70" />
                </button>
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 z-10 border border-gray-200 dark:border-gray-700">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{currentUser.displayName || 'User'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{currentUser.email}</p>
                  </div>
                  <Link 
                    to="/profile" 
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                  >
                    <User className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                    Profile
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                  >
                    <LogOut className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                    Sign out
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2 ml-1">
                <Link 
                  to="/signin" 
                  className="px-3 py-1.5 text-gray-700 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Sign in
                </Link>
                <Link 
                  to="/signup" 
                  className="px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white rounded-md shadow-sm hover:shadow transition-all duration-200"
                >
                  Sign up
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile header right items */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5 text-amber-500" />
              ) : (
                <Moon className="h-5 w-5 text-indigo-600" />
              )}
            </button>
            
            {/* Mobile menu button */}
            <button
              onClick={toggleMenu}
              className="p-2 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none"
            >
              {isOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div 
        className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen 
            ? 'max-h-screen border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900' 
            : 'max-h-0'
        }`}
      >
        <nav className="px-4 py-3 flex flex-col space-y-3">
          <Link 
            to="/" 
            className="flex items-center px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400"
            onClick={() => setIsOpen(false)}
          >
            <Home className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
            Home
          </Link>
          <a 
            href="https://kangleicode.in" 
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400"
            onClick={() => setIsOpen(false)}
          >
            <Globe className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
            KangleiCode Home
          </a>
          
          <div className="border-t border-gray-200 dark:border-gray-800 pt-2">
            <p className="px-3 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Tools
            </p>
            {tools.filter(tool => isFeatureEnabled(tool.id)).map((tool) => (
              <Link 
                key={tool.path}
                to={tool.path} 
                className="flex items-center px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400"
                onClick={() => setIsOpen(false)}
              >
                <tool.icon className="h-5 w-5 mr-3 text-indigo-500 dark:text-indigo-400" />
                {tool.name}
              </Link>
            ))}
          </div>
          
          {currentUser && (
            <>
              <Link 
                to="/dashboard" 
                className="flex items-center px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400"
                onClick={() => setIsOpen(false)}
              >
                <BarChart className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                Dashboard
              </Link>
              
              <div className="border-t border-gray-200 dark:border-gray-800"></div>
              
              <div className="px-3 py-2">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center mr-2">
                    <User className="h-4 w-4 text-indigo-700 dark:text-indigo-300" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{currentUser.displayName || 'User'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{currentUser.email}</p>
                  </div>
                </div>
                
                <Link 
                  to="/profile" 
                  className="flex items-center px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400"
                  onClick={() => setIsOpen(false)}
                >
                  <User className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                  Profile
                </Link>
                
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center px-3 py-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  <LogOut className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                  Sign out
                </button>
              </div>
            </>
          )}
          
          {!currentUser && (
            <div className="border-t border-gray-200 dark:border-gray-800 pt-3 grid grid-cols-2 gap-2 px-3">
              <Link 
                to="/signin" 
                className="flex justify-center py-2 text-gray-700 dark:text-white border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Sign in
              </Link>
              <Link 
                to="/signup" 
                className="flex justify-center py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white rounded-md"
                onClick={() => setIsOpen(false)}
              >
                Sign up
              </Link>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
